import Icon from '@/components/atoms/Icon';

interface RecordingRowProps {
  id: string;
  title: string;
  label?: string;
  isOriginal?: boolean;
  showActionButton: boolean;
  isActive: boolean;
  onSelect: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  isDownloading?: boolean;
  isDeleting?: boolean;
}

function RecordingRow({
  title,
  label,
  isActive,
  isOriginal,
  showActionButton,
  onSelect,
  onDownload,
  onDelete,
  isDownloading = false,
  isDeleting = false
}: RecordingRowProps) {
  // The action buttons live inside the clickable row, so stop the click from also selecting the recording.
  const handleAction = (e: React.MouseEvent, action?: () => void) => {
    e.stopPropagation();
    action?.();
  };

  // While either action is in flight both buttons are locked to avoid overlapping requests.
  const actionsDisabled = isDownloading || isDeleting;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className="flex cursor-pointer items-center gap-2 rounded px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
    >
      <span className="shrink-0">
        <Icon name={isOriginal ? 'IconOriginalRecording' : 'IconCopy'} size={20} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{label ?? title}</span>
      </div>
      {showActionButton && (
        <div>
          <button
            onClick={e => handleAction(e, onDownload)}
            disabled={actionsDisabled}
            title="Download"
            className="rounded p-1 text-slate-500 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon
              name={isDownloading ? 'IconLoader2' : 'IconDownload'}
              size={20}
              className={isDownloading ? 'animate-spin' : ''}
            />
          </button>
          <button
            onClick={e => handleAction(e, onDelete)}
            disabled={actionsDisabled}
            title="Delete"
            className="rounded p-1 text-slate-500 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon
              name={isDeleting ? 'IconLoader2' : 'IconTrash'}
              size={20}
              className={isDeleting ? 'animate-spin' : ''}
            />
          </button>
        </div>
      )}
      {isActive && (
        <span className="flex w-4 shrink-0 items-center justify-center">
          <Icon name="IconCheck" size={20} />
        </span>
      )}
    </div>
  );
}

export default RecordingRow;
